/**
 * SeeMe Toast Notifications
 * Lightweight transient feedback messages.
 *
 * Usage:
 *   SeeMe.toast('Saved successfully', { type: 'success' })
 *   SeeMe.toast.success('Saved successfully')
 *   SeeMe.toast.error('Something went wrong')
 *   SeeMe.toast.warning('Low balance')
 *   SeeMe.toast.info('New feature available')
 *   SeeMe.toast.dark('Audio muted')
 *
 * Options:
 *   type      — 'success' | 'error' | 'warning' | 'info' | 'dark' (default: 'success')
 *   duration  — ms before auto-dismiss (default: 3000; 0 = persistent)
 *   icon      — show icon (default: true)
 *   position  — 'bottom-center' | 'bottom-right' | 'bottom-left' |
 *               'top-center' | 'top-right' | 'top-left' (default: 'bottom-center')
 *   offset    — px nudge from the primary edge.
 *               Number: overrides top for top-* positions, bottom for bottom-* positions.
 *               Object: { top, right, bottom, left } for full control.
 *   iconClass — override the default icon class string (e.g. 'icon ri-volume-mute-line').
 *               Useful when the page uses a different icon library than Font Awesome.
 *   container — DOM element or CSS selector. Toast container is appended inside
 *               that element (position: absolute) instead of body (position: fixed).
 *               The element must have position: relative and overflow: visible.
 *
 * Page-level defaults (set once, apply to all subsequent toasts on the page):
 *   SeeMe.toast.position  = 'top-right';
 *   SeeMe.toast.offset    = 60;
 *   SeeMe.toast.container = '#myPanel';
 *
 * Global shorthand (for HTMX inline handlers):
 *   showToast('Saved!', 'success')
 */

var SeeMe = window.SeeMe || {};

SeeMe.toast = (function () {
    var MAX_VISIBLE = 3;
    var DEFAULT_POSITION = 'bottom-center';
    var containers = {};                   // position → el (global, body-appended)
    var scopedContainers = new WeakMap(); // parentEl → { position → el }

    var ICONS = {
        success: 'fa-solid fa-circle-check',
        error:   'fa-solid fa-circle-xmark',
        warning: 'fa-solid fa-triangle-exclamation',
        info:    'fa-solid fa-circle-info',
        dark:    'fa-solid fa-circle-info'
    };

    // Resolve a container option to a DOM element (accepts element or CSS selector)
    function resolveEl(val) {
        if (!val) return null;
        if (typeof val === 'string') return document.querySelector(val);
        return val;
    }

    // Apply offset as inline styles, overriding whatever the CSS class sets.
    // Number → primary edge for the position (top for top-*, bottom for bottom-*).
    // Object → apply each provided key { top, right, bottom, left }.
    function applyOffset(el, position, offset) {
        if (offset == null) return;
        if (typeof offset === 'number') {
            var edge = position.indexOf('top') === 0 ? 'top' : 'bottom';
            el.style[edge] = offset + 'px';
        } else {
            if (offset.top    != null) el.style.top    = offset.top    + 'px';
            if (offset.right  != null) el.style.right  = offset.right  + 'px';
            if (offset.bottom != null) el.style.bottom = offset.bottom + 'px';
            if (offset.left   != null) el.style.left   = offset.left   + 'px';
        }
    }

    function getContainer(position, parentEl, offset) {
        var cls = position === 'bottom-center'
            ? 'toast-container'
            : 'toast-container toast-container--' + position;
        var el;

        if (parentEl) {
            // Scoped mode: toast container lives inside parentEl, uses position: absolute
            var map = scopedContainers.get(parentEl);
            if (!map) { map = {}; scopedContainers.set(parentEl, map); }

            if (!map[position]) {
                el = parentEl.querySelector(
                    position === 'bottom-center'
                        ? '.toast-container:not([class*="--"])'
                        : '.toast-container--' + position
                );
                if (!el) {
                    el = document.createElement('div');
                    el.className = cls;
                    parentEl.appendChild(el);
                }
                el.style.position = 'absolute';
                map[position] = el;
            }
            applyOffset(map[position], position, offset);
            return map[position];
        }

        // Global mode: appended to body, position: fixed via CSS class
        if (!containers[position]) {
            el = document.querySelector('.toast-container--' + position)
              || (position === 'bottom-center' ? document.querySelector('.toast-container:not([class*="--"])') : null);
            if (!el) {
                el = document.createElement('div');
                el.className = cls;
                document.body.appendChild(el);
            }
            containers[position] = el;
        }
        applyOffset(containers[position], position, offset);
        return containers[position];
    }

    function dismiss(el) {
        el.classList.add('toast--leaving');
        el.addEventListener('animationend', function () {
            if (el.parentNode) el.parentNode.removeChild(el);
        });
    }

    function enforceLimit(container) {
        var toasts = container.querySelectorAll('.toast:not(.toast--leaving)');
        while (toasts.length > MAX_VISIBLE) {
            dismiss(toasts[0]);
            toasts = container.querySelectorAll('.toast:not(.toast--leaving)');
        }
    }

    function show(message, opts) {
        opts = opts || {};
        var type     = opts.type     || 'success';
        var duration = opts.duration != null ? opts.duration : 3000;
        var showIcon = opts.icon !== false;
        var position = opts.position  || show.position  || DEFAULT_POSITION;
        var parentEl = resolveEl(opts.container != null ? opts.container : show.container);
        var offset   = opts.offset   != null ? opts.offset   : show.offset;

        var iconCls  = opts.iconClass != null ? opts.iconClass : ICONS[type];

        var el = document.createElement('div');
        el.className = 'toast toast--' + type;

        if (showIcon && iconCls) {
            var icon = document.createElement('i');
            icon.className = iconCls;
            el.appendChild(icon);
        }

        var span = document.createElement('span');
        span.textContent = message;
        el.appendChild(span);

        var container = getContainer(position, parentEl, offset);
        container.appendChild(el);
        enforceLimit(container);

        if (duration > 0) {
            setTimeout(function () { dismiss(el); }, duration);
        }

        return el;
    }

    // Page-level defaults (set before showing any toasts on the page)
    show.position  = DEFAULT_POSITION;
    show.offset    = null;
    show.container = null;

    // Shorthand methods
    show.success = function (msg, opts) { opts = opts || {}; opts.type = 'success'; return show(msg, opts); };
    show.error   = function (msg, opts) { opts = opts || {}; opts.type = 'error';   return show(msg, opts); };
    show.warning = function (msg, opts) { opts = opts || {}; opts.type = 'warning'; return show(msg, opts); };
    show.info    = function (msg, opts) { opts = opts || {}; opts.type = 'info';    return show(msg, opts); };
    show.dark    = function (msg, opts) { opts = opts || {}; opts.type = 'dark';    return show(msg, opts); };

    return show;
})();

window.SeeMe = SeeMe;

// Global shorthand for HTMX inline handlers
window.showToast = function (message, type) {
    SeeMe.toast(message, { type: type || 'success' });
};
